import { jwtDecode } from 'jwt-decode';
import { firebaseApp } from '../auth/FirebaseApp';
import { config } from '../config';
import {
  clearExpectedUserId,
  matchesExpectedUserId,
  setExpectedUserId,
  setSignOutReason,
  setSkipAutoLogin,
} from './sessionIdentity';

let accessToken = '';
let managerAccessToken = '';
export const getAccessToken = () => {
  return accessToken;
};

export const setAccessToken = (token: string) => {
  accessToken = token;
};

export const isImpersonatedSession = () => {
  try {
    return !!jwtDecode<{ impersonatedBy?: string }>(accessToken).impersonatedBy;
  } catch {
    return false;
  }
};

export const getManagerAccessToken = () => {
  return managerAccessToken;
};

export const setManagerAccessToken = (token: string) => {
  managerAccessToken = token;
};

const refreshTokenUrl = config.VITE_GRAPHQL_URL
  ? config.VITE_GRAPHQL_URL + '/refresh_token'
  : 'https://graphql-mog42n2lwa-lz.a.run.app/refresh_token';

const getIdToken = async (authDomain: string): Promise<string | null> => {
  const fb = await firebaseApp.initialize({ authDomain });
  const user = await fb.currentUser();
  return user ? await user.getIdToken() : null;
};

export const forceSignOut = (reason: string) => {
  clearExpectedUserId();
  setSkipAutoLogin();
  setSignOutReason(reason);
  setAccessToken('');
  setManagerAccessToken('');
  window.location.href = `/signin?reason=${reason}`;
  return { ok: false, accessToken: '', refreshToken: '' };
};

export const fetchAccessToken = async () => {
  const p = await fetch(refreshTokenUrl + '/provider', { credentials: 'include' });
  const providerInfo = await p.json();

  const body =
    !providerInfo.provider || providerInfo.provider === 'password'
      ? {}
      : { token: await getIdToken(providerInfo.authDomain) };

  const r = await fetch(refreshTokenUrl, {
    method: 'POST',
    credentials: 'include',
    body: JSON.stringify(body),
    headers: {
      'content-type': 'application/json',
    },
  });

  const response = await r.json();

  if (response.ok && response.accessToken) {
    if (!matchesExpectedUserId(response.accessToken)) {
      return forceSignOut('session-ended');
    }
    setExpectedUserId(response.accessToken);
  }

  await tryManagerRefreshToken(response.refreshToken);

  return { ...response };
};

export const isValid = async (token: string) => {
  if (!token) {
    return false;
  }
  try {
    const { exp } = jwtDecode(token);
    if (Date.now() >= (exp ?? 0) * 1000) {
      return false;
    } else {
      return true;
    }
  } catch {
    return false;
  }
};

export const tryManagerRefreshToken = async (refreshToken: string) => {
  try {
    const response = await fetch(`${config.VITE_MANAGER_API_URL}/v1/auth/manager-refresh-token`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({ refreshToken, embedded: true }),
      headers: {
        'content-type': 'application/json',
      },
    });
    const data = await response.json();
    setManagerAccessToken(data.accessToken);
  } catch {
    console.warn('Could not refresh manager token');
  }
};
